import { defineAsyncComponent } from 'vue'

// Function to dynamically resolve the path to the component based on the template name
function loadModule(componentName) {
  const template = window.vueAppConfig?.settings?.template ?? 'default'
  return defineAsyncComponent(() =>
    import(
      /* webpackChunkName: "component-[request]" */ `@/components/template/${template}/${componentName}.vue`
    )

      .then(component => component?.default ?? null)
      .catch(error => {
        // If the current template is not 'default', try loading from the default template folder as a fallback
        if (template !== 'default') {
          return import(
            /* webpackChunkName: "component-[request]-default" */ `@/components/default/${componentName}.vue`
          )
            .then(component => component?.default ?? null)
        }
        // If already using default or fallback fails, re-throw the error
        throw error
      })
  )
}

export const moduleConfig = {
  default: {
    upcoming: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/CountdownModule'),
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/ContentModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/cta/UpcomingModule'),
          () => loadModule('module/main/FooterModule')
        ]
      },
      isNotSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule'),
          () => loadModule('module/cta/JoinSidebarModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/CountdownModule'),
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/ContentModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/cta/JoinModule'),
          () => loadModule('module/cta/UpcomingModule'),
          () => loadModule('module/main/FooterModule')
        ]
      }
    },
    live: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: false,
        topBar: false,
        bottomBar: false,
        mainContent: [() => loadModule('module/main/JitsiModule')]
      },
      isNotSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule'),
          () => loadModule('module/cta/JoinSidebarModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/ContentModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/cta/JoinModule'),
          () => loadModule('module/cta/UpcomingModule'),
          () => loadModule('module/main/FooterModule')
        ]
      }
    },
    ended: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/PlaybackModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/main/ReviewModule'),
          () => loadModule('module/cta/UpcomingModule'),
          () => loadModule('module/main/FooterModule')
        ]
      },
      isNotSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule'),
          () => loadModule('module/cta/JoinSidebarModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/ContentModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/main/ReviewModule'),
          () => loadModule('module/cta/JoinModule'),
          () => loadModule('module/cta/UpcomingModule'),
          () => loadModule('module/main/FooterModule')
        ]
      }
    }
  },

  zoom: {
    live: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: false,
        topBar: false,
        bottomBar: false,
        mainContent: [() => loadModule('module/main/ZoomModule')]
      }
    }
  },

  ticket: {
    upcoming: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/TicketModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/main/ReviewModule'),
          () => loadModule('module/main/FooterModule')
        ]
      }
    },

    live: {
      isSignedUp: {
        leftSidebar: false,
        rightSidebar: [
          () => loadModule('module/sidebar/PromoVideoModule'),
          () => loadModule('module/sidebar/About/BrandModule'),
          () => loadModule('module/sidebar/About/DateModule'),
          () => loadModule('module/sidebar/About/PriceModule'),
          () => loadModule('module/sidebar/LecturersModule'),
          () => loadModule('module/sidebar/stages/StagesModule'),
          () => loadModule('module/sidebar/FollowModule')
        ],
        topBar: [() => loadModule('module/bar/HeaderModule')],
        bottomBar: false,
        mainContent: [
          () => loadModule('module/main/BannerModule'),
          () => loadModule('module/main/TicketModule'),
          () => loadModule('module/main/AttachmentsModule'),
          () => loadModule('module/main/ReviewModule')
        ]
      }
    }
  }
  // Add more configurations as needed
}
